"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { useIsHydrated } from "@/hooks/useIsHydrated";
import { type ConsentPreferences, type ConsentState, DEFAULT_CONSENT, CONSENT_VERSION, CONSENT_STORAGE_KEY } from "@/lib/consent/types";
interface ConsentContextValue {
  /** Current consent preferences */
  preferences: ConsentPreferences;
  /** Whether consent has been given (banner dismissed) */
  hasConsented: boolean;
  /** Whether the consent banner should be shown */
  showBanner: boolean;
  /** Accept all cookies */
  acceptAll: () => void;
  /** Accept only necessary cookies */
  acceptNecessary: () => void;
  /** Update specific consent preferences */
  updatePreferences: (preferences: Partial<ConsentPreferences>) => void;
  /** Open the consent settings (show banner again) */
  openSettings: () => void;
  /** Check if a specific category is consented */
  hasConsent: (category: keyof ConsentPreferences) => boolean;
}
const ConsentContext = createContext<ConsentContextValue | null>(null);

/**
 * Load consent state from localStorage
 */
function loadConsentState(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;
    const state = JSON.parse(stored) as ConsentState;

    // Validate version and structure
    if (state.version !== CONSENT_VERSION || !state.preferences) {
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

/**
 * Save consent state to localStorage
 */
function saveConsentState(preferences: ConsentPreferences): void {
  if (typeof window === "undefined") return;
  const state: ConsentState = {
    preferences,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
}

/**
 * Get initial consent state on client
 */
function getInitialConsentState(): {
  preferences: ConsentPreferences;
  hasConsented: boolean;
} {
  const state = loadConsentState();
  if (state) {
    return {
      preferences: state.preferences,
      hasConsented: true
    };
  }
  return {
    preferences: DEFAULT_CONSENT,
    hasConsented: false
  };
}
interface ConsentProviderProps {
  children: ReactNode;
}
export function ConsentProvider(t0) {
  const $ = _c(14);
  const {
    children
  } = t0;
  const isHydrated = useIsHydrated();
  const [preferences, setPreferences] = useState(_temp);
  const [hasConsented, setHasConsented] = useState(_temp2);
  const [settingsRequested, setSettingsRequested] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const allAccepted = {
        necessary: true,
        analytics: true,
        marketing: true
      };
      setPreferences(allAccepted);
      setHasConsented(true);
      setSettingsRequested(false);
      saveConsentState(allAccepted);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const acceptAll = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const onlyNecessary = {
        necessary: true,
        analytics: false,
        marketing: false
      };
      setPreferences(onlyNecessary);
      setHasConsented(true);
      setSettingsRequested(false);
      saveConsentState(onlyNecessary);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const acceptNecessary = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = newPreferences => {
      setPreferences(prev => {
        const updated = {
          ...prev,
          ...newPreferences,
          necessary: true
        };
        saveConsentState(updated);
        return updated;
      });
      setHasConsented(true);
      setSettingsRequested(false);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const updatePreferences = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setSettingsRequested(true);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const openSettings = t4;
  let t5;
  if ($[4] !== preferences) {
    t5 = category => preferences[category];
    $[4] = preferences;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const hasConsent = t5;
  const computedShowBanner = isHydrated && (!hasConsented || settingsRequested);
  const t6 = isHydrated ? preferences : DEFAULT_CONSENT;
  const t7 = isHydrated ? hasConsented : false;
  let t8;
  if ($[6] !== computedShowBanner || $[7] !== hasConsent || $[8] !== t6 || $[9] !== t7) {
    t8 = {
      preferences: t6,
      hasConsented: t7,
      showBanner: computedShowBanner,
      acceptAll,
      acceptNecessary,
      updatePreferences,
      openSettings,
      hasConsent
    };
    $[6] = computedShowBanner;
    $[7] = hasConsent;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== children || $[12] !== t8) {
    t9 = <ConsentContext.Provider value={t8}>{children}</ConsentContext.Provider>;
    $[11] = children;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}
function _temp2() {
  if (typeof window === "undefined") {
    return false;
  }
  return getInitialConsentState().hasConsented;
}
function _temp() {
  if (typeof window === "undefined") {
    return DEFAULT_CONSENT;
  }
  return getInitialConsentState().preferences;
}
export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return context;
}

/**
 * Hook to check if analytics consent has been given
 */
export function useAnalyticsConsent() {
  const $ = _c(3);
  const {
    hasConsent,
    hasConsented
  } = useConsent();
  let t0;
  if ($[0] !== hasConsent || $[1] !== hasConsented) {
    t0 = hasConsented && hasConsent("analytics");
    $[0] = hasConsent;
    $[1] = hasConsented;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/**
 * Hook to check if marketing consent has been given
 */
export function useMarketingConsent() {
  const $ = _c(3);
  const {
    hasConsent,
    hasConsented
  } = useConsent();
  let t0;
  if ($[0] !== hasConsent || $[1] !== hasConsented) {
    t0 = hasConsented && hasConsent("marketing");
    $[0] = hasConsent;
    $[1] = hasConsented;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}